import React, { useState, Suspense } from 'react'
import { useApp } from '../context/AppContext'
import { FlameKnight, ShadowNinja, CyberMech, ForestDruid, StormMonk } from '../components/characters'

export function Characters() {
  const { state, dispatch, characterArchetypes } = useApp()
  const [showCreator, setShowCreator] = useState(false)
  const [newCharacter, setNewCharacter] = useState({
    name: '',
    archetype: 'flameKnight',
  })

  const currentUser = state.users.find(u => u.id === state.currentUserId)
  const myCharacters = state.characters.filter(c => c.userId === state.currentUserId)

  const handleCreate = (e) => {
    e.preventDefault()
    if (!newCharacter.name.trim()) return

    const archetype = characterArchetypes[newCharacter.archetype]
    const character = {
      id: `char_${Date.now()}`,
      userId: state.currentUserId,
      name: newCharacter.name.trim(),
      archetype: newCharacter.archetype,
      ...archetype.stats,
      maxJumps: archetype.maxJumps,
      specialName: archetype.specialName,
      createdAt: Date.now(),
    }

    dispatch({ type: 'ADD_CHARACTER', payload: character })
    setNewCharacter({ name: '', archetype: 'flameKnight' })
    setShowCreator(false)
  }

  const handleDelete = (id) => {
    if (confirm('Delete this fighter?')) {
      dispatch({ type: 'DELETE_CHARACTER', payload: id })
    }
  }

  const handleSelect = (id, isCpu = false) => {
    if (isCpu) {
      dispatch({ type: 'SELECT_CPU_CHARACTER', payload: id })
    } else {
      dispatch({ type: 'SELECT_CHARACTER', payload: id })
    }
  }

  if (!currentUser) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold mb-4">Sign in to Create Fighters</h2>
        <p className="text-gray-400">Create an account to build your roster</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Characters</h1>
          <p className="text-gray-400">Build your fighter roster</p>
        </div>
        <button
          onClick={() => setShowCreator(true)}
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all"
        >
          + New Fighter
        </button>
      </div>

      {/* Character Creator Modal */}
      {showCreator && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#131a33] border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Create New Fighter</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={newCharacter.name}
                  onChange={(e) => setNewCharacter({ ...newCharacter, name: e.target.value })}
                  className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500/50"
                  placeholder="Fighter name"
                  maxLength={20}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Fighter Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(characterArchetypes).map(([key, archetype]) => {
                      const CharacterSvg = {
                        flameKnight: FlameKnight,
                        shadowNinja: ShadowNinja,
                        cyberMech: CyberMech,
                        forestDruid: ForestDruid,
                        stormMonk: StormMonk,
                      }[key]
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setNewCharacter({ ...newCharacter, archetype: key })}
                          className={`p-3 rounded-lg text-left transition-all ${
                            newCharacter.archetype === key
                              ? 'bg-cyan-500/20 border border-cyan-500/50'
                              : 'bg-black/30 border border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="h-24 mb-2">
                            <Suspense fallback={<div className="h-full bg-white/5 rounded" />}>
                              <CharacterSvg className="w-full h-full" />
                            </Suspense>
                          </div>
                          <div className="font-medium text-sm">{archetype.label}</div>
                          <div className="text-xs text-gray-500">{archetype.role}</div>
                        </button>
                      )
                    })}
                </div>
              </div>
              <div className="p-3 bg-black/30 rounded-lg">
                <p className="text-sm text-gray-400">
                  {characterArchetypes[newCharacter.archetype].description}
                </p>
                <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                  {Object.entries(characterArchetypes[newCharacter.archetype].stats).map(([stat, value]) => (
                    <div key={stat} className="text-center">
                      <div className="text-gray-500 uppercase">{stat}</div>
                      <div className="font-bold">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreator(false)}
                  className="flex-1 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Character Grid */}
      {myCharacters.length === 0 ? (
        <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10 border-dashed">
          <div className="text-4xl mb-4">⚔️</div>
          <h3 className="text-xl font-bold mb-2">No Fighters Yet</h3>
          <p className="text-gray-400 mb-4">Create your first fighter to start battling</p>
          <button
            onClick={() => setShowCreator(true)}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg"
          >
            Create Fighter
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {myCharacters.map((character) => {
            const archetype = characterArchetypes[character.archetype]
            const isSelected = state.selectedCharacterId === character.id
            const isCpuSelected = state.selectedCpuCharacterId === character.id

            const CharacterSvg = {
              flameKnight: FlameKnight,
              shadowNinja: ShadowNinja,
              cyberMech: CyberMech,
              forestDruid: ForestDruid,
              stormMonk: StormMonk,
            }[character.archetype] || FlameKnight

            return (
              <div
                key={character.id}
                className={`relative bg-gradient-to-br from-white/10 to-white/5 border rounded-xl p-4 transition-all ${
                  isSelected
                    ? 'border-cyan-500/50 shadow-lg shadow-cyan-500/20'
                    : isCpuSelected
                    ? 'border-pink-500/50 shadow-lg shadow-pink-500/20'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                {/* Selection Badges */}
                {isSelected && (
                  <div className="absolute -top-2 -right-2 px-2 py-1 bg-cyan-500 text-black text-xs font-bold rounded-full">
                    P1
                  </div>
                )}
                {isCpuSelected && (
                  <div className="absolute -top-2 -right-2 px-2 py-1 bg-pink-500 text-white text-xs font-bold rounded-full">
                    CPU
                  </div>
                )}

                {/* Character Visual */}
                <div className="h-32 mb-3 flex items-center justify-center">
                  <Suspense fallback={<div className="h-full w-full bg-white/5 rounded" />}>
                    <CharacterSvg className="h-full w-auto" />
                  </Suspense>
                </div>

                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-lg">{character.name}</h3>
                    <span className="text-xs text-gray-400">{archetype.label}</span>
                  </div>
                  <button
                    onClick={() => handleDelete(character.id)}
                    className="text-gray-500 hover:text-red-400 transition-colors"
                  >
                    ×
                  </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 mb-3 text-center text-xs">
                  {Object.entries(archetype.stats).map(([stat, value]) => (
                    <div key={stat} className="bg-black/30 rounded p-1">
                      <div className="text-gray-500 uppercase text-[10px]">{stat}</div>
                      <div className="font-bold">{value}</div>
                    </div>
                  ))}
                </div>

                {/* Special Move */}
                <div className="text-xs text-gray-400 mb-3">
                  Special: <span className="text-cyan-400">{character.specialName}</span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSelect(character.id, false)}
                    disabled={isSelected}
                    className={`flex-1 py-1.5 text-xs font-bold rounded transition-all ${
                      isSelected
                        ? 'bg-cyan-500/20 text-cyan-400 cursor-default'
                        : 'bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20'
                    }`}
                  >
                    {isSelected ? 'Selected P1' : 'Select P1'}
                  </button>
                  <button
                    onClick={() => handleSelect(character.id, true)}
                    disabled={isCpuSelected}
                    className={`flex-1 py-1.5 text-xs font-bold rounded transition-all ${
                      isCpuSelected
                        ? 'bg-pink-500/20 text-pink-400 cursor-default'
                        : 'bg-pink-500/10 text-pink-400 hover:bg-pink-500/20'
                    }`}
                  >
                    {isCpuSelected ? 'Selected CPU' : 'Select CPU'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
